import { useUserContext } from "@/contexts/UserContextProvider";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useState } from "react";
import NotesUploadModal from "./NotesUploadModel";

interface ActionsContainerProps {
  title: string;
  info?: string;
  steps: string[];
  children: (
    currentStep: number,
    setCurrentStep: (step: number) => void,
  ) => React.ReactNode;
  showSteps?: boolean;
  showNotes?: boolean;
  taskShotIds?: string[];
  refetch?: () => void;
  showBrief?: boolean;
  setShowUploadModal?: React.Dispatch<React.SetStateAction<boolean>>;
  onStepChange?: (
    currentStep: number,
    targetStep: number,
    setCurrentStep: (step: number) => void,
  ) => void;
}

function ActionsContainer({
  title,
  // info,
  steps,
  children,
  showSteps = true,
  showNotes = false,
  taskShotIds,
  refetch,
  showBrief = false,
  setShowUploadModal,
  onStepChange,
}: ActionsContainerProps) {
  const { currentProject } = useUserContext();
  const [currentStep, setCurrentStep] = useState<number>(0);

  const handleStepChange = (targetStep: number) => {
    if (onStepChange) {
      onStepChange(currentStep, targetStep, setCurrentStep);
    } else {
      setCurrentStep(targetStep);
    }
  };

  return (
    <div className="p-6">
      <div className="font-medium text-xl mb-5">
        {currentProject?.name || "Project"}
      </div>

      <div className="bg-accordion rounded-3xl overflow-hidden">
        <div className="p-6 flex justify-between">
          <div className="font-medium text-xl">{title}</div>

          {showSteps && (
            <div className="flex items-center gap-2">
              {steps
                .filter((key) => isNaN(Number(key)))
                .map((step, index) => (
                  <div
                    key={step}
                    className={cn(
                      "border rounded-full p-2 flex items-center gap-1 text-xs",
                      index < currentStep && "cursor-pointer",
                      index === currentStep &&
                        "border-service-paint text-service-paint",
                    )}
                    onClick={() => {
                      if (index < currentStep) {
                        handleStepChange(index);
                      }
                    }}
                  >
                    <div
                      className={cn(
                        "text-white bg-secondary rounded-full size-5 flex items-center justify-center",
                        index <= currentStep && "bg-service-paint",
                      )}
                    >
                      {currentStep > index ? <Check size={12} /> : index + 1}
                    </div>
                    <div>{step}</div>
                  </div>
                ))}
            </div>
          )}

          {showNotes && taskShotIds && refetch && (
            <NotesUploadModal taskShotIds={taskShotIds} onRefech={refetch} />
          )}

          {showBrief && setShowUploadModal && (
            <div
              className="w-fit ml-auto cursor-pointer hover:text-service-paint mr-6"
              onClick={(e) => {
                e.stopPropagation();
                setShowUploadModal(true);
              }}
            >
              Use a spreadsheet to fill this
            </div>
          )}
        </div>

        <div>{children(currentStep, setCurrentStep)}</div>
      </div>
    </div>
  );
}

export default ActionsContainer;
